import { FormattedMessage, useIntl } from 'react-intl'
import MarkDownMessage from '@gluedigital/markdown-message'
import { useState, FormEvent, useEffect } from 'react'
import { useDialog } from '@hooks/useDialog'
import useAnalyticsProperty from '@hooks/useAnalyticsProperty'
import { useNavigate } from 'react-router'
import Loader from '@components/Loader/Loader'

import errorDialog from '@static/images/dialog/error-dialog.png'
import successDialog from '@static/images/dialog/success-dialog.png'
import './ProfileAnalyticsDialog.css'

type DialogStep = 'form' | 'success' | 'error'

interface ProfileAnalyticsDialogProps {
  isOpen: boolean
  onClose: () => void
  isBlocking?: boolean
}

export default function ProfileAnalyticsDialog({
  isOpen,
  onClose,
  isBlocking = false
}: ProfileAnalyticsDialogProps) {
  const { dialogRef, handleBackdropClick } = useDialog(isOpen, onClose)
  const navigate = useNavigate()
  const intl = useIntl()
  const { submitProperty, refreshProperty } = useAnalyticsProperty()

  const [step, setStep] = useState<DialogStep>('form')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setStep('form')
      setIsLoading(false)
    }
  }, [isOpen])

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const property = formData.get('property')
    if (typeof property !== 'string' || property.trim().length === 0) {
      return
    }
    setIsLoading(true)
    try {
      await submitProperty(property)
      await refreshProperty()
      setStep('success')
    } catch {
      setStep('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <dialog
      id="profile-analytics-dialog"
      className="app-dialog"
      ref={dialogRef}
      onClick={isBlocking ? undefined : handleBackdropClick}
      onCancel={(e) => {
        if (isBlocking) {
          e.preventDefault()
        } else {
          onClose()
        }
      }}
    >
      <div className="dialog-inner">
        {step === 'form' ? (
          <>
            <h2>
              <span className="icon icon-lock-closed" />
              <span>
                <FormattedMessage id="profile.analytics-dialog.form-title" />
              </span>
            </h2>
            <p>
              <FormattedMessage id="profile.analytics-dialog.form-text" />
            </p>
            <a
              href="/pdf/users_guide.pdf"
              download="users_guide.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-link"
            >
              <FormattedMessage id="profile.analytics-dialog.error-link" />
            </a>
            <form onSubmit={handleSubmit} className="dialog-step">
              <label>
                <h3>
                  <MarkDownMessage id="profile.analytics-dialog.form-label" />
                </h3>
                <input
                  name="property"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{9}"
                  maxLength={9}
                  title={intl.formatMessage({
                    id: 'profile.analytics-dialog.form-example'
                  })}
                  placeholder={intl.formatMessage({
                    id: 'profile.analytics-dialog.form-placeholder'
                  })}
                  disabled={isLoading}
                  required
                />
                <span>
                  <FormattedMessage id="profile.analytics-dialog.form-example" />
                </span>
              </label>
              {isLoading ? (
                <Loader mini />
              ) : (
                <button
                  type="submit"
                  className="highlight-button"
                  disabled={isLoading}
                >
                  <FormattedMessage id="profile.analytics-dialog.form-button" />
                </button>
              )}
            </form>
          </>
        ) : (
          <div className={`dialog-step result-view result-view-${step}`}>
            <img
              src={step === 'success' ? successDialog : errorDialog}
              alt={step === 'success' ? 'Success' : 'Error'}
              className="result-image"
            />
            <h2>
              <FormattedMessage id={`profile.analytics-dialog.${step}-title`} />
            </h2>
            <p>
              <FormattedMessage id={`profile.analytics-dialog.${step}-text`} />
            </p>
            {step === 'error' && (
              <a
                href="/pdf/users_guide.pdf"
                download="users_guide.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-link"
              >
                <FormattedMessage id="profile.analytics-dialog.error-link" />
              </a>
            )}
            <div className="buttons">
              <button
                className={`${step}-button`}
                type="button"
                onClick={
                  step === 'success'
                    ? () => navigate('/')
                    : () => setStep('form')
                }
              >
                <FormattedMessage
                  id={`profile.analytics-dialog.${step}-button`}
                />
              </button>
              {step === 'success' && (
                <button onClick={() => setStep('form')} type="button">
                  <FormattedMessage
                    id={`profile.analytics-dialog.success-button-add`}
                  />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </dialog>
  )
}
